/**
 * Doc-Match Strictness — DMS-5 combined telemetry surface.
 *
 * Aggregates the two in-process counters that DMS exposes:
 *   - DM2 Gemini-scorer (`relevanceScoring.ts`):
 *       cacheHits / cacheMisses / timeouts / errors / filenameShortCircuits
 *   - DAT chip-render side (`docApplicability.ts`):
 *       filenameNegativeSuppressed / strongClaimRejected
 *
 * Kept in its own module (rather than colocated with `adminTelemetry.ts`)
 * because `docApplicability.ts` pulls in `REPORT_TYPE_REGISTRY` via the
 * static checklist registry, and several AdminPanel tests mock the
 * `config/reportTypes` module partially. Importing the strictness
 * summary lazily — and from a module the AdminPanel doesn't directly
 * touch — keeps those tests stable.
 *
 * Counters live in process memory and reset on cold start. A
 * persistent / per-tenant rollup is deferred to
 * DMS-FOLLOWUP-PERSISTENT-METRICS.
 */
import { getScoringTelemetry, type ScoringTelemetry } from './relevanceScoring';
import {
  getDocMatchStrictnessTelemetry,
  type DocMatchStrictnessTelemetry,
} from '../../lib/docApplicability';

export interface DocMatchStrictnessSummary {
  /** DM2 telemetry (cache hits / misses / timeouts / errors /
   *  filename short-circuits). Mirrors `getScoringTelemetry()`. */
  scoring: ScoringTelemetry;
  /** DAT telemetry (filename gate fires + strong-claim rejections). */
  applicability: DocMatchStrictnessTelemetry;
}

/**
 * Read both telemetry surfaces in a single call. Pure / synchronous —
 * counters live in process memory.
 *
 * The two surfaces SHOULD track each other when DM2 scoring is eager
 * (every (doc, requirement) pair gets a fresh score). When they
 * diverge, DAT is catching false positives DM2 missed — useful feedback
 * for tuning the per-requirement negative-regex map.
 */
export function getDocMatchStrictnessSummary(): DocMatchStrictnessSummary {
  return {
    scoring: getScoringTelemetry(),
    applicability: getDocMatchStrictnessTelemetry(),
  };
}
